import { Vec2 } from '../../math/vec2.js';
import { Seat } from './seat.js';
import { SeatingLabel } from './seating-label.js';
import { SeatGroup } from './seat-group.js';
import { SeatingElement } from './seating-element.js';
import { SerializedData, isSameData, isSameList } from '../../dto/dto.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { fromEnumAsInt, toEnumFromInt } from '../../../serialization/enum-utils.js';

export enum SeatRowNamingStrategy {
  Sequential = 1,
  LeftRight = 2,
  LeftRightMiddle = 3,
}

export class SeatRow extends SeatGroup {
  static readonly TYPE_ID: number = 0x02;

  public readonly namingStrategy: SeatRowNamingStrategy;
  public readonly firstLabelingOrdinal: number;

  label: SeatingLabel | null;

  constructor(
      uniqueId: number,
      enabled: boolean,
      position: Vec2,
      rotation: number,
      label: SeatingLabel | null,
      namingStrategy: SeatRowNamingStrategy,
      seats: Seat[],
      firstLabelingOrdinal: number | null = null,
      parent: WeakRef<SeatingElement> | null = null,
  ) {
    super(uniqueId, enabled, position, rotation, parent, seats);
    this.label = label;
    this.namingStrategy = namingStrategy;
    this.firstLabelingOrdinal = firstLabelingOrdinal ?? 1;
  }

  get $typeId(): number {
    return SeatRow.TYPE_ID;
  }

  static fromJson(data: SerializedData): SeatRow {
    const result = new SeatRow(
        MapUtils.get<number>(data, 'uniqueId'),
        MapUtils.get<boolean>(data, 'enabled'),
        Vec2.fromJson(MapUtils.get<SerializedData>(data, 'position')),
        MapUtils.getNumber(data, 'rotation'),
        SeatingLabel.fromJsonOrNull(MapUtils.getOrNull<SerializedData>(data, 'label')),
        toEnumFromInt(MapUtils.getNumber(data, 'namingStrategy'), SeatRowNamingStrategy),
        MapUtils.get<SerializedData[]>(data, 'seats').map((data) => Seat.fromJson(data)),
        MapUtils.getOrNull<number>(data, 'firstLabelingOrdinal'),
    );

    result.label?.setParent(result);
    for(const seat of result.seats) {
      seat.setParent(result);
    }

    return result;
  }

  toJson(): SerializedData {
    return {
      '$typeId': this.$typeId,
      'uniqueId': this.uniqueId,
      'enabled': this.enabled,
      'position': this.position.toJson(),
      'rotation': this.rotation,
      'label': this.label?.toJson() ?? null,
      'namingStrategy': fromEnumAsInt(this.namingStrategy),
      'seats': this.seats.map((seat) => seat.toJson()),
      'firstLabelingOrdinal': this.firstLabelingOrdinal,
    };
  }

  clone(parent: WeakRef<SeatingElement> | null): this {
    const result = new SeatRow(
        this.uniqueId,
        this.enabled,
        this.position.clone(),
        this.rotation,
        this.label?.clone(null) ?? null,
        this.namingStrategy,
        this.seats?.map((seat) => seat.clone(null)) ?? null,
        this.firstLabelingOrdinal,
        parent,
    ) as this;

    result.label?.setParent(result);
    for(const seat of result.seats) {
      seat.setParent(result);
    }

    return result;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return this.uniqueId === other.uniqueId &&
        this.enabled === other.enabled &&
        isSameData(this.position, other.position) &&
        this.rotation === other.rotation &&
        isSameData(this.label, other.label) &&
        this.namingStrategy === other.namingStrategy &&
        isSameList(this.seats, other.seats, (a, b) => isSameData(a, b)) &&
        this.firstLabelingOrdinal === other.firstLabelingOrdinal;
  }
}
